import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import { Input } from '../../../../core/design-system/components/Input';
import { Button } from '../../../../core/design-system/components/Button';
import { Text } from '../../../../core/design-system/components/Text';
import { usePhoneVerification } from '../../../../core/hooks/usePhoneVerification';
import './PhoneStep.css';
import './common.css';

const PhoneStep = ({ onComplete, initialValue = '' }) => {
  const { t, i18n } = useTranslation('onboarding');
  const [phone, setPhone] = useState(initialValue);
  const [verificationCode, setVerificationCode] = useState('');
  const [localError, setLocalError] = useState('');
  
  const {
    sendVerificationCode,
    verifyCode,
    isLoading,
    isVerifying,
    error,
    verificationSent,
    cooldownRemaining
  } = usePhoneVerification();

  // Obtenir le pays par défaut basé sur la langue
  const getDefaultCountry = () => {
    const language = i18n.language || 'fr';
    const languageToCountry = {
      fr: 'FR',
      en: 'US',
      es: 'ES',
      de: 'DE',
      it: 'IT'
    };
    
    return languageToCountry[language.split('-')[0]] || 'FR';
  };
  
  // Valider le numéro de téléphone
  const validatePhoneNumber = (phoneNumber) => {
    try {
      if (!phoneNumber.startsWith('+')) {
        return isValidPhoneNumber(phoneNumber, getDefaultCountry());
      }
      return isValidPhoneNumber(phoneNumber);
    } catch {
      return false;
    }
  };
  
  // Formatter le numéro pour l'affichage et le stockage
  const formatPhoneNumber = (phoneNumber) => {
    try {
      const defaultCountry = getDefaultCountry();
      const parsedNumber = phoneNumber.startsWith('+')
        ? parsePhoneNumberFromString(phoneNumber)
        : parsePhoneNumberFromString(phoneNumber, defaultCountry);
      
      return parsedNumber ? parsedNumber.format('E.164') : phoneNumber;
    } catch {
      return phoneNumber;
    }
  };
  
  // Envoyer le code de vérification
  const handleSendCode = async () => {
    // Try validation with detected country first, then fallback to FR
    let isValid = validatePhoneNumber(phone);
    let formatted = formatPhoneNumber(phone);

    // If validation fails with default country (e.g. US for EN locale),
    // retry with FR since most users are French
    if (!isValid && getDefaultCountry() !== 'FR') {
      const asFR = phone.startsWith('+') ? phone : phone;
      if (isValidPhoneNumber(asFR, 'FR')) {
        isValid = true;
        formatted = parsePhoneNumberFromString(asFR, 'FR')?.format('E.164') || asFR;
      }
    }

    if (!isValid) {
      setLocalError(t('phone.errors.invalid_number'));
      return;
    }

    setLocalError('');
    setPhone(formatted);
    await sendVerificationCode(formatted);
  };
  
  // Vérifier le code
  const handleVerifyCode = async () => {
    const result = await verifyCode(verificationCode);
    if (result.success) {
      onComplete(result);
    }
  };
  
  return (
    <div className="phone-step-container">
      <div className="phone-step-content">
        {!verificationSent ? (
          <>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setLocalError(''); }}
              placeholder={t('phone.placeholder')}
              error={localError || error}
              disabled={isLoading}
              className="phone-input"
            />
            
            <Text variant="caption" className="phone-format-hint">
              {t('phone.format_hint')}
            </Text>
          </>
        ) : (
          <>
            <Text variant="body2" className="code-sent-message">
              {t('phone.code_sent', { phone })}
            </Text>
            
            <Input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder={t('phone.code_placeholder')}
              error={error}
              disabled={isVerifying}
              className="code-input"
            />

            {cooldownRemaining > 0 && (
              <Text variant="caption" className="resend-countdown">
                {t('phone.resend_countdown', { seconds: cooldownRemaining })}
              </Text>
            )}
          </>
        )}
      </div>

      <div className="button-container">
        <Button 
          onClick={!verificationSent ? handleSendCode : handleVerifyCode}
          variant="primary"
          disabled={
            !verificationSent
              ? !phone || phone.length < 6 || isLoading
              : !verificationCode || isVerifying
          }
          className={`step-submit-button ${(isLoading || isVerifying) ? 'loading' : ''}`}
        >
          {isLoading || isVerifying
            ? t('common.loading')
            : t(!verificationSent ? 'phone.send_code' : 'phone.verify_code')}
        </Button>
      </div>
    </div>
  );
};

export default PhoneStep; 